import type { NextFetchInit, BaseAirport, Region } from ".";
import { ODB_KEYS } from ".";
import { queryOptions } from "@tanstack/react-query";
import type { getDestinations } from "@jb-dotcom/api/odb/destinations";
import type { getOrigins } from "@jb-dotcom/api/odb/origins";
import { TransformPipeline } from "@jb-dotcom/transformers";
import type { getAirportsByCountry } from "./countries";

const splitRegions = (airports: BaseAirport[]) => {
  const regions: (Region & { key: string })[] = [];
  const regionalAirports: Record<
    string,
    Omit<BaseAirport, "cc"> & { key: string; searchString: string }
  > = {};
  const localAirports: (Omit<BaseAirport, "cc"> & {
    key: string;
    searchString: string;
  })[] = [];

  for (const { cc, ...airport } of airports) {
    if (cc) {
      regions.push({ ...airport, cc, key: airport.code });
    } else if ("pm" in airport) {
      regionalAirports[airport.code] = {
        ...airport,
        key: airport.code,
        searchString: [
          ...new Set(
            [airport.name, airport.code, airport.needle]
              .join(",")
              .toLowerCase()
              .split(","),
          ),
        ].join(","),
      };
    } else {
      localAirports.push({
        ...airport,
        key: airport.code,
        searchString: [
          ...new Set(
            [airport.name, airport.code, airport.needle]
              .join(",")
              .toLowerCase()
              .split(","),
          ),
        ].join(","),
      });
    }
  }

  return {
    localAirports,
    regionalAirports,
    regions,
  };
};

const mapRegions = ({
  regionalAirports,
  regions,
}: Pick<ReturnType<typeof splitRegions>, "regions" | "regionalAirports">) =>
  regions.map(({ cc, needle, name, code, ...airport }) => {
    const children = cc
      .map((code) => regionalAirports[code])
      .filter(Boolean as unknown as <T>(a: T | undefined) => a is T)
      .toSorted((a, b) => a.name.localeCompare(b.name));

    return {
      ...airport,
      cc: children,
      code,
      name,
      needle: [needle, ...cc].join(","),
      searchString: [
        ...new Set([
          ...[name, code, needle].join(",").toLowerCase().split(","),
          ...children.flatMap((c) => c.searchString),
        ]),
      ].join(","),
    };
  });

const sortMappedRegions = (
  mappedRegions: ReturnType<typeof mapRegions>,
  localAirports: ReturnType<typeof splitRegions>["localAirports"],
  priorityCode?: string,
) => {
  const sorted: (
    | (typeof mappedRegions)[number]
    | (typeof localAirports)[number]
  )[] = [...mappedRegions, ...localAirports].toSorted((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (priorityCode) {
    const normalizedPriorityCode = priorityCode.toUpperCase();
    const priorityIndex = sorted.findIndex(
      ({ key, ...rest }) =>
        key === normalizedPriorityCode ||
        ("cc" in rest &&
          rest.cc.some((c) => c.code === normalizedPriorityCode)),
    );

    if (sorted[priorityIndex]) {
      return [sorted[priorityIndex], ...sorted.toSpliced(priorityIndex, 1)];
    }
  }
  return sorted;
};

export type Airport = {
  cc?: Airport[];
  code: string;
  countryCode?: string;
  name: string;
  needle: string;
  searchString: string;
  jb: boolean;
};

const groupRegions = (
  airports: BaseAirport[],
  priorityCode?: string,
): Airport[] => {
  const { regions, regionalAirports, localAirports } = splitRegions(airports);
  const mappedRegions = mapRegions({ regionalAirports, regions });
  return sortMappedRegions(mappedRegions, localAirports, priorityCode);
};

export const transformData = (
  destinationsData: Awaited<
    ReturnType<typeof getDestinations>
  >["data"]["destinations"],
  originsData: Awaited<ReturnType<typeof getOrigins>>["data"]["origins"],
  airportsByCountry: Awaited<ReturnType<typeof getAirportsByCountry>>,
  priorityCode?: string,
) =>
  TransformPipeline.new({
    destinationsData,
    originsData,
  })
    .next(({ originsData, destinationsData }) => {
      const s = new Set();
      const filtered = [];
      const merged = [...originsData, ...destinationsData];
      for (const airport of merged) {
        if (!s.has(airport.code)) {
          s.add(airport.code);
          filtered.push(airport);
        }
      }
      return filtered;
    })
    .next((filteredAirports) => {
      for (const airport of filteredAirports) {
        airport.countryCode = airportsByCountry[airport.code];
      }
      return filteredAirports;
    })
    .next((filteredAirports) => groupRegions(filteredAirports, priorityCode));
export type TransformedData = ReturnType<typeof transformData>["value"];
export const getTransformedData = async (origin: string, baseUrl = "") => {
  const response = await fetch(`${baseUrl}/api/odb-data/${origin}`, {
    cache: "force-cache",
    next: {
      revalidate: 600,
      tags: [...ODB_KEYS(), "transformed", origin],
    },
  } as NextFetchInit);
  if (!response.ok) {
    throw new Error(`Failed to fetch data: ${response.statusText}`);
  }
  return (await response.json()) as TransformedData;
};
export const transformedDataOptions = (origin: string, baseUrl = "") =>
  queryOptions({
    queryFn: () => getTransformedData(origin, baseUrl),
    queryKey: [...ODB_KEYS(), "transformed", origin],
    staleTime: Infinity,
  });
